import { memo, useCallback, useMemo } from 'react';
import { stopNotificationSound } from 'notification-sounds';
import { MessagesIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import styled from 'foundations-theming/utils';
import CloseButton from './CloseButton';
import { getContextText } from '../utils/ContextUtil';
import { sanitizeHtml } from '../utils/HTMLManipulation';
import StyledTeaseBase from './StyledTeaseBase';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledTeaseNotification = styled(StyledTeaseBase)(({
  theme,
  animationDurationMs
}) => {
  return {
    display: 'flex',
    background: theme.color['fill-surface-default'],
    borderRadius: '0.1875rem',
    boxShadow: `4px 0 22px 0 rgba(66, 91, 118, 0.21)`,
    zIndex: 1111,
    minHeight: theme.space['1400'],
    overflowY: 'hidden',
    transition: `max-height ${animationDurationMs}ms ease-in-out`,
    position: 'relative',
    padding: theme.space['400'],
    '&:hover': {
      cursor: 'pointer'
    }
  };
});
const StyledNotificationContent = styled.a.withConfig({
  displayName: "TeaseDisplay__StyledNotificationContent",
  componentId: "wd0t49-0"
})(({
  theme
}) => {
  return {
    display: 'flex',
    flexDirection: 'column',
    marginInlineEnd: theme.space['800'],
    border: 'none',
    background: 'inherit',
    textAlign: 'start',
    textDecoration: 'none',
    color: theme.color['text-core-default'],
    '&:hover': {
      color: theme.color['text-core-default'],
      textDecoration: 'none'
    }
  };
});
const StyledNotificationSubject = styled.div.withConfig({
  displayName: "TeaseDisplay__StyledNotificationSubject",
  componentId: "wd0t49-1"
})(({
  theme
}) => {
  return {
    color: theme.color['text-core-default'],
    fontWeight: theme.typography['heading-100'].fontWeight,
    marginLeft: theme.space['300'],
    lineHeight: '150%'
  };
});

// // The webkit styles are what allows the ellipsis truncation to clip on the 2nd line
// // Although not standard they are implemented across our target browsers and are not
// // expected to go away in the near future. Should any or all of them be removed the
// // fallback of max-height and overflow would still keep the box height and clipping
// // we would just lose the ellipsis character.
const StyledNotificationBody = styled.div.withConfig({
  displayName: "TeaseDisplay__StyledNotificationBody",
  componentId: "wd0t49-2"
})(({
  theme
}) => {
  return {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    fontWeight: theme.typography['body-100'].fontWeight,
    marginInlineStart: theme.space['900'],
    marginTop: theme.space['200'],
    lineHeight: '150%',
    maxHeight: theme.space['1000']
  };
});
const StyledIconSection = styled.div.withConfig({
  displayName: "TeaseDisplay__StyledIconSection",
  componentId: "wd0t49-3"
})(({
  theme
}) => {
  return {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.color['fill-accent-neutral-default'],
    borderRadius: '50%',
    justifyContent: 'center',
    minHeight: theme.space['600'],
    padding: theme.space['100'],
    minWidth: theme.space['600'],
    fill: theme.color['icon-core-on-fill-default'],
    '& > *': {
      transform: 'translateY(1px)'
    }
  };
});
const StyledCloseIcon = styled(CloseButton)(({
  theme
}) => {
  return {
    right: theme.space['400'],
    top: theme.space['400']
  };
});
const TeaseDisplay = ({
  animationDurationMs,
  body,
  context,
  displayState,
  isExample,
  isHovered,
  linkUrl,
  notificationType,
  subject,
  handleAnimationEnd,
  handleCloseNotification,
  handleCloseOnClick,
  onMouseEnter,
  onMouseLeave,
  onNotificationClicked,
  renderDropdown
}) => {
  const handleDismissTease = useCallback(() => {
    handleCloseNotification();
    stopNotificationSound();
  }, [handleCloseNotification]);
  const onCloseClick = useCallback(() => {
    handleCloseOnClick();
    stopNotificationSound();
  }, [handleCloseOnClick]);
  const sanitizedContextText = useMemo(() => {
    const contextText = getContextText(context, notificationType) || body;
    return contextText && sanitizeHtml(contextText, []);
  }, [context, notificationType, body]);
  const sanitizedSubject = useMemo(() => {
    return subject && sanitizeHtml(subject, []);
  }, [subject]);
  if (displayState === 'closed') {
    return null;
  }
  const smallerClickArea = !sanitizedContextText && !isExample;
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(StyledTeaseNotification, {
      "aria-live": "assertive",
      animationDurationMs: animationDurationMs,
      "data-test-id": "tease-notification",
      isOpen: displayState === 'open',
      onAnimationEnd: handleAnimationEnd,
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave,
      role: "alert",
      children: [/*#__PURE__*/_jsxs(StyledNotificationContent, {
        "data-test-id": "tease-notification-content",
        onClick: onNotificationClicked,
        href: linkUrl,
        role: 'menuitem',
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          flex: '1 1 auto',
          display: "flex",
          children: [/*#__PURE__*/_jsx(StyledIconSection, {
            children: /*#__PURE__*/_jsx(MessagesIcon, {
              size: "100",
              color: "fill-surface-default"
            })
          }), /*#__PURE__*/_jsx(StyledNotificationSubject, {
            "data-test-id": "tease-notification-subject",
            children: sanitizedSubject
          })]
        }),
        /* When there is no content text, e.g. the example notification then don't render
         * an element as the margin would throw off the alignment of the header in the popup
         */
        sanitizedContextText ?
        /*#__PURE__*/
        // @bend-expect trellis-tokenized
        _jsx(StyledNotificationBody, {
          "data-test-id": "tease-notification-body",
          children: sanitizedContextText
        }) : null]
      }), isHovered ? /*#__PURE__*/_jsx(StyledCloseIcon, {
        onClick: onCloseClick,
        smallerClickArea: smallerClickArea
      }) : null, !isExample ? renderDropdown({
        handleDismissTease,
        smallerClickArea
      }) : null]
    })
  );
};
export default /*#__PURE__*/memo(TeaseDisplay);